import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import APIPlayground from '../components/APIPlayground';
import DemoWidget from '../components/DemoWidget';
import CSVReportDownload from '../components/CSVReportDownload';
import VerifiedOutcomesShell from '../components/VerifiedOutcomesShell';
import {
  RefreshCw,
  Activity,
  Filter,
  Calendar,
  Info,
  Key,
  Copy,
  Trash2,
  Plus,
  TrendingUp,
  CheckCircle,
  FileText,
  Zap,
  Target,
  Radio,
  CreditCard,
  Play
} from 'lucide-react';

const API = 'https://mizar-api.vercel.app';

async function fetchBillingStatus(user) {
  if (!user?.id || !user?.email) {
    return {
      found: false,
      subscription_status: 'inactive',
      tier: null,
      monthly_limit: null,
      stripe_customer_id: null,
      stripe_subscription_id: null,
      trial_ends_at: null,
      period_start: null,
      period_end: null
    };
  }

  const res = await fetch(API + '/v1/billing/status', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: user.email, user_id: user.id })
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.detail || data?.error || `Billing status failed (${res.status})`);
  }

  return data;
}

async function fetchTierFromSupabase(supabase, user) {
  if (!supabase || !user?.id) return null;

  const { data, error } = await supabase
    .from('user_tiers')
    .select('tier, monthly_limit, period_start, period_end, stripe_customer_id, stripe_subscription_id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) {
    console.error('Error reading user_tiers:', error);
    return null;
  }

  if (!data?.tier) return null;

  return {
    found: true,
    subscription_status: 'active',
    tier: String(data.tier).toLowerCase(),
    monthly_limit: data.monthly_limit || 0,
    stripe_customer_id: data.stripe_customer_id || null,
    stripe_subscription_id: data.stripe_subscription_id || null,
    trial_ends_at: null,
    period_start: data.period_start || null,
    period_end: data.period_end || null
  };
}

function BillingTab({ supabase, user, onBillingRefresh }) {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [portalLoading, setPortalLoading] = React.useState(false);
  const [subscribing, setSubscribing] = React.useState(false);
  const [selectedTier, setSelectedTier] = React.useState('growth');
  const [email, setEmail] = React.useState(user?.email || '');

  async function loadBillingStatus() {
    if (!user) {
      setStatus(null);
      setLoading(false);
      return;
    }

    try {
      let billing = null;

      try {
        billing = await fetchBillingStatus(user);
      } catch (e) {
        console.error('Billing endpoint failed, falling back to Supabase:', e);
      }

      if (
        billing?.found &&
        billing?.tier &&
        ['trialing', 'active', 'past_due', 'canceled', 'unpaid'].includes(
          billing.subscription_status
        )
      ) {
        let calls = 0;

        if (billing.period_start) {
          const usageRes = await supabase
            .from('api_usage')
            .select('id')
            .eq('user_id', user.id)
            .gte('timestamp', billing.period_start);

          calls = usageRes.data?.length || 0;
        }

        const nextStatus = { ...billing, calls_used: calls };
        setStatus(nextStatus);
        onBillingRefresh?.(nextStatus);
        return;
      }

      const tierFallback = await fetchTierFromSupabase(supabase, user);

      if (tierFallback?.found && tierFallback?.tier) {
        let calls = 0;

        if (tierFallback.period_start) {
          const usageRes = await supabase
            .from('api_usage')
            .select('id')
            .eq('user_id', user.id)
            .gte('timestamp', tierFallback.period_start);

          calls = usageRes.data?.length || 0;
        }

        const nextStatus = { ...tierFallback, calls_used: calls };
        setStatus(nextStatus);
        onBillingRefresh?.(nextStatus);
      } else {
        setStatus(null);
        onBillingRefresh?.(null);
      }
    } catch (e) {
      console.error('Error loading billing status:', e);
      setStatus(null);
      onBillingRefresh?.(null);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (user?.email) setEmail(user.email);
    loadBillingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase, user]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout !== 'success' || !user?.id) return;

    let cancelled = false;
    let attempts = 0;

    async function pollAfterCheckout() {
      while (!cancelled && attempts < 12) {
        attempts += 1;
        try {
          let billing = null;

          try {
            billing = await fetchBillingStatus(user);
          } catch (err) {
            console.error('Post-checkout billing endpoint failed:', err);
          }

          if (
            billing?.found &&
            billing?.tier &&
            ['trialing', 'active'].includes(billing.subscription_status)
          ) {
            await loadBillingStatus();
            const url = new URL(window.location.href);
            url.searchParams.delete('checkout');
            window.history.replaceState({}, '', url.toString());
            return;
          }

          const fallback = await fetchTierFromSupabase(supabase, user);
          if (fallback?.found && fallback?.tier) {
            await loadBillingStatus();
            const url = new URL(window.location.href);
            url.searchParams.delete('checkout');
            window.history.replaceState({}, '', url.toString());
            return;
          }
        } catch (err) {
          console.error('Post-checkout billing poll failed:', err);
        }

        await new Promise((resolve) => setTimeout(resolve, 2500));
      }
    }

    pollAfterCheckout();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, supabase]);

  async function openPortal() {
    if (!user?.id || !user?.email) {
      window.alert('You must be logged in to manage billing.');
      return;
    }

    setPortalLoading(true);
    try {
      const res = await fetch(API + '/v1/billing/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, user_id: user.id })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || data?.error || `Portal failed (${res.status})`);
      }

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      throw new Error('Billing portal did not return a URL.');
    } catch (e) {
      console.error('Billing portal error:', e);
      window.alert(e?.message || 'Failed to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  }

  async function startTrial() {
    const checkoutEmail = (email || user?.email || '').trim();
    const checkoutUserId = user?.id;

    if (!checkoutEmail || !checkoutUserId) {
      window.alert('No valid logged-in user found for checkout. Please sign in again.');
      return;
    }

    setSubscribing(true);
    try {
      const res = await fetch(API + '/v1/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: selectedTier,
          email: checkoutEmail,
          user_id: checkoutUserId
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || data?.error || `Checkout failed (${res.status})`);
      }

      if (!data?.url) {
        throw new Error('Checkout did not return a Stripe URL.');
      }

      window.location.href = data.url;
    } catch (e) {
      console.error('Checkout error:', e);
      window.alert(e?.message || 'Failed to start checkout.');
    } finally {
      setSubscribing(false);
    }
  }

  const statusColors = {
    trialing: '#2F9E80',
    active: '#3BA876',
    past_due: '#C87B2F',
    canceled: '#C0392B',
    unpaid: '#C0392B'
  };

  const tierLabels = {
    payg: '£0.08/call',
    growth: '£299/mo · 10K calls',
    scale: '£799/mo · 50K calls',
    enterprise: 'Custom plan'
  };

  const tierDescriptions = {
    payg: 'Start light',
    growth: 'Best for pilots',
    scale: 'High volume',
    enterprise: 'Custom support'
  };

  if (loading) {
    return (
      <div
        style={{
          color: 'var(--ink2)',
          padding: '40px 0',
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '14px'
        }}
      >
        Loading billing...
      </div>
    );
  }

  if (!status?.tier) {
    return (
      <div style={{ maxWidth: '620px' }}>
        <div className="card" style={{ padding: '28px' }}>
          <h3
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '1.1rem',
              marginBottom: '6px'
            }}
          >
            No active subscription
          </h3>

          <p
            style={{
              color: 'var(--ink2)',
              fontSize: '14px',
              marginBottom: '24px'
            }}
          >
            Start a 14-day free trial. No charge until it ends.
          </p>

          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                fontSize: '11px',
                color: 'var(--ink2)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 600,
                marginBottom: '10px'
              }}
            >
              Select tier
            </div>

            <div className="billing-tier-tabs">
              {['payg', 'growth', 'scale'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setSelectedTier(t)}
                  className={`billing-tier-tab ${selectedTier === t ? 'active' : ''}`}
                >
                  <div className="billing-tier-tab-name">{t}</div>
                  <div className="billing-tier-tab-price">{tierLabels[t]}</div>
                  <div className="billing-tier-tab-note">{tierDescriptions[t]}</div>
                </button>
              ))}
            </div>

            <input
              type="email"
              placeholder="Work email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              style={{ marginBottom: '12px', width: '100%' }}
            />

            <button
              onClick={startTrial}
              disabled={subscribing || !email || !user?.id}
              className="btn-primary"
              style={{ width: '100%' }}
            >
              {subscribing ? 'Starting...' : 'Start 14-day free trial'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const limit = status.monthly_limit || 0;
  const used = status.calls_used || 0;
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const statusColor = statusColors[status.subscription_status] || 'var(--ink3)';

  return (
    <div style={{ maxWidth: '520px', display: 'grid', gap: '16px' }}>
      <div className="card" style={{ padding: '28px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px'
          }}
        >
          <div>
            <div
              style={{
                fontSize: '11px',
                color: 'var(--ink2)',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 600,
                marginBottom: '4px'
              }}
            >
              Current plan
            </div>

            <div
              style={{
                fontSize: '22px',
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 800,
                color: 'var(--fire)',
                textTransform: 'uppercase'
              }}
            >
              {status.tier}
            </div>
          </div>

          <span
            style={{
              background: statusColor + '18',
              border: '1px solid ' + statusColor + '40',
              borderRadius: '20px',
              padding: '3px 12px',
              color: statusColor,
              fontSize: '11px',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              letterSpacing: '0.06em'
            }}
          >
            {status.subscription_status}
          </span>
        </div>

        {status.trial_ends_at && (
          <div
            style={{
              marginBottom: '16px',
              padding: '10px 14px',
              background: 'rgba(47,158,128,0.06)',
              border: '1px solid rgba(47,158,128,0.2)',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#2F9E80'
            }}
          >
            Trial ends{' '}
            {new Date(status.trial_ends_at).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            })}
          </div>
        )}

        {limit > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '6px',
                fontSize: '12px'
              }}
            >
              <span style={{ color: 'var(--ink2)' }}>API calls this period</span>
              <span style={{ color: pct > 80 ? '#C87B2F' : 'var(--ink2)' }}>
                {used.toLocaleString()} / {limit.toLocaleString()}
              </span>
            </div>

            <div
              style={{
                height: '4px',
                background: 'var(--line)',
                borderRadius: '2px',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: pct + '%',
                  background: pct > 80 ? '#C87B2F' : '#3BA876',
                  borderRadius: '2px',
                  transition: 'width 0.6s ease'
                }}
              />
            </div>

            <div
              style={{
                fontSize: '11px',
                color: 'var(--ink3)',
                marginTop: '4px'
              }}
            >
              {Math.max(0, limit - used).toLocaleString()} remaining · resets{' '}
              {status.period_end
                ? new Date(status.period_end).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short'
                  })
                : '—'}
            </div>
          </div>
        )}

        <button onClick={openPortal} disabled={portalLoading} className="btn-secondary">
          {portalLoading ? 'Opening...' : 'Manage subscription →'}
        </button>
      </div>

      <div className="card" style={{ padding: '20px 24px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            fontSize: '13px'
          }}
        >
          {[
            [
              'Period start',
              status.period_start
                ? new Date(status.period_start).toLocaleDateString('en-GB')
                : '—'
            ],
            [
              'Period end',
              status.period_end
                ? new Date(status.period_end).toLocaleDateString('en-GB')
                : '—'
            ]
          ].map(([label, val]) => (
            <div key={label}>
              <div
                style={{
                  fontSize: '10px',
                  color: 'var(--ink2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 600,
                  marginBottom: '4px'
                }}
              >
                {label}
              </div>
              <div style={{ fontWeight: 600 }}>{val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Console() {
  const { supabase, user } = useAuth();

  const [activeTab, setActiveTab] = useState('overview');
  const [overviewStats, setOverviewStats] = useState({
    decisionsSupported: 0,
    estimatedSavings: 0,
    highRiskSignals: 0,
    conversionRate: 0
  });

  const [stats, setStats] = useState({
    activeRoutes: 0,
    avgRisk: 0,
    highRiskCount: 0,
    totalSnapshots: 0
  });

  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    date: 'latest',
    origin: 'all',
    risk: 'all'
  });

  const [apiKeys, setApiKeys] = useState([]);
  const [showNewKeyForm, setShowNewKeyForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyEnv, setNewKeyEnv] = useState('live');
  const [generatedKey, setGeneratedKey] = useState(null);

  const [analyticsData, setAnalyticsData] = useState({
    precision: 0,
    recall: 0,
    falsePositiveRate: 0,
    userBehaviorByRisk: {
      high: { bookedNow: 0, delayed: 0, abandoned: 0 },
      medium: { bookedNow: 0, delayed: 0, abandoned: 0 },
      low: { bookedNow: 0, delayed: 0, abandoned: 0 }
    }
  });

  const [topRoutes, setTopRoutes] = useState([]);
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [hasConsoleAccess, setHasConsoleAccess] = useState(false);

  async function loadConsoleAccess() {
    if (!user) {
      setHasConsoleAccess(false);
      setBillingLoaded(true);
      return;
    }

    try {
      let billing = null;

      try {
        billing = await fetchBillingStatus(user);
      } catch (err) {
        console.error('Billing endpoint failed, falling back to Supabase:', err);
      }

      if (
        billing?.found &&
        billing?.tier &&
        ['trialing', 'active'].includes(billing.subscription_status)
      ) {
        setHasConsoleAccess(true);
        setBillingLoaded(true);
        return;
      }

      const tierFallback = await fetchTierFromSupabase(supabase, user);

      if (tierFallback?.found && tierFallback?.tier) {
        setHasConsoleAccess(true);
      } else {
        setHasConsoleAccess(false);
      }
    } catch (err) {
      console.error('Error loading console access:', err);
      setHasConsoleAccess(false);
    } finally {
      setBillingLoaded(true);
    }
  }

  useEffect(() => {
    loadConsoleAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, supabase]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout !== 'success' || !user?.id) return;

    let cancelled = false;
    let attempts = 0;

    async function pollConsoleUnlock() {
      while (!cancelled && attempts < 12) {
        attempts += 1;
        try {
          let billing = null;

          try {
            billing = await fetchBillingStatus(user);
          } catch (err) {
            console.error('Console unlock billing endpoint failed:', err);
          }

          if (
            billing?.found &&
            billing?.tier &&
            ['trialing', 'active'].includes(billing.subscription_status)
          ) {
            setHasConsoleAccess(true);
            setBillingLoaded(true);

            const url = new URL(window.location.href);
            url.searchParams.delete('checkout');
            window.history.replaceState({}, '', url.toString());

            if (activeTab === 'billing') setActiveTab('overview');
            return;
          }

          const fallback = await fetchTierFromSupabase(supabase, user);
          if (fallback?.found && fallback?.tier) {
            setHasConsoleAccess(true);
            setBillingLoaded(true);

            const url = new URL(window.location.href);
            url.searchParams.delete('checkout');
            window.history.replaceState({}, '', url.toString());

            if (activeTab === 'billing') setActiveTab('overview');
            return;
          }
        } catch (err) {
          console.error('Console unlock poll failed:', err);
        }

        await new Promise((resolve) => setTimeout(resolve, 2500));
      }
    }

    pollConsoleUnlock();
    return () => {
      cancelled = true;
    };
  }, [user, activeTab, supabase]);

  useEffect(() => {
    if (billingLoaded && !hasConsoleAccess && activeTab !== 'billing') {
      setActiveTab('billing');
    }
  }, [billingLoaded, hasConsoleAccess, activeTab]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!billingLoaded) return;

    if (!hasConsoleAccess) {
      if (activeTab !== 'billing') setActiveTab('billing');
      setLoading(false);
      return;
    }

    if (activeTab === 'overview') {
      fetchOverviewData();
    } else if (activeTab === 'signals') {
      fetchLiveData();
      const interval = setInterval(fetchLiveData, 30000);
      return () => clearInterval(interval);
    } else if (activeTab === 'api-keys') {
      fetchApiKeys();
    } else if (activeTab === 'analytics') {
      fetchAnalyticsData();
    }
  }, [filters, activeTab, billingLoaded, hasConsoleAccess]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchOverviewData = async () => {
    try {
      setLoading(true);

      const startOfMonth = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        1
      ).toISOString();

      const { data: decisionsData, error: decisionsError } = await supabase
        .from('user_decisions')
        .select('origin_iata, destination_iata, regret_risk_score, user_action')
        .eq('user_id', user.id)
        .gte('decision_timestamp', startOfMonth);

      if (decisionsError) throw decisionsError;

      const decisionsSupported = decisionsData?.length || 0;
      const highRiskSignals =
        decisionsData?.filter((d) => Number(d.regret_risk_score) >= 0.7).length || 0;
      const estimatedSavings = Math.round(highRiskSignals * 285 * 0.1);
      const bookedNow =
        decisionsData?.filter((d) => d.user_action === 'booked_now').length || 0;
      const conversionRate =
        decisionsSupported > 0
          ? Math.round((bookedNow / decisionsSupported) * 100)
          : 0;

      const routeMap = {};
      (decisionsData || []).forEach((d) => {
        const routeKey = `${d.origin_iata}-${d.destination_iata}`;
        if (!routeMap[routeKey]) {
          routeMap[routeKey] = { count: 0, highRiskCount: 0 };
        }
        routeMap[routeKey].count += 1;
        if (Number(d.regret_risk_score) >= 0.7) {
          routeMap[routeKey].highRiskCount += 1;
        }
      });

      const topRoutesArray = Object.entries(routeMap)
        .map(([route, data]) => ({
          route,
          decisions: data.count,
          highRiskPct: Math.round((data.highRiskCount / data.count) * 100)
        }))
        .sort((a, b) => b.decisions - a.decisions)
        .slice(0, 5);

      setTopRoutes(topRoutesArray);

      setOverviewStats({
        decisionsSupported,
        estimatedSavings,
        highRiskSignals,
        conversionRate
      });
    } catch (err) {
      console.error('Error fetching overview:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveData = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('market_predictions')
        .select('*')
        .order('regret_risk_score', { ascending: false })
        .order('snapshot_date', { ascending: false });

      if (filters.date === 'today') {
        query = query.eq('snapshot_date', new Date().toISOString().split('T')[0]);
      } else if (filters.date === 'week') {
        query = query.gte(
          'snapshot_date',
          new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0]
        );
      }

      if (filters.origin !== 'all') {
        query = query.eq('origin_iata', filters.origin);
      }

      const { data, error } = await query.limit(100);
      if (error) throw error;

      const signalsWithRisk = (data || []).map((s) => ({
        ...s,
        riskScore: Math.round(Number(s.regret_risk_score || 0) * 100)
      }));

      let filteredSignals = signalsWithRisk;
      if (filters.risk === 'high') {
        filteredSignals = signalsWithRisk.filter((s) => s.riskScore >= 70);
      } else if (filters.risk === 'medium') {
        filteredSignals = signalsWithRisk.filter(
          (s) => s.riskScore >= 40 && s.riskScore < 70
        );
      } else if (filters.risk === 'low') {
        filteredSignals = signalsWithRisk.filter((s) => s.riskScore < 40);
      }

      setSignals(filteredSignals);

      const riskScores = filteredSignals.map((s) => s.riskScore);

      setStats({
        activeRoutes: new Set(
          filteredSignals.map((s) => `${s.origin_iata}-${s.destination_iata}`)
        ).size,
        totalSnapshots: filteredSignals.length,
        avgRisk:
          riskScores.length > 0
            ? Math.round(riskScores.reduce((a, b) => a + b, 0) / riskScores.length)
            : 0,
        highRiskCount: riskScores.filter((r) => r >= 70).length
      });
    } catch (err) {
      console.error('Error fetching signals:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('api_keys_v2')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys(data || []);
    } catch (err) {
      console.error('Error fetching API keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      const thirtyDaysAgo = new Date(
        Date.now() - 30 * 24 * 60 * 60 * 1000
      ).toISOString();

      const { data: decisionsData, error: decisionsError } = await supabase
        .from('user_decisions')
        .select('decision_id, regret_risk_score, user_action')
        .eq('user_id', user.id)
        .gte('decision_timestamp', thirtyDaysAgo);

      if (decisionsError) throw decisionsError;

      const decisionIds = (decisionsData || [])
        .map((d) => d.decision_id)
        .filter(Boolean);

      let outcomesData = [];

      if (decisionIds.length > 0) {
        const { data: outcomes, error: outcomesError } = await supabase
          .from('outcome_verification')
          .select('decision_id, prediction_outcome, ground_truth_rose')
          .in('decision_id', decisionIds)
          .gte('verification_timestamp', thirtyDaysAgo);

        if (outcomesError) throw outcomesError;
        outcomesData = outcomes || [];
      }

      const tp = outcomesData.filter((o) => o.prediction_outcome === 'TP').length;
      const fp = outcomesData.filter((o) => o.prediction_outcome === 'FP').length;
      const tn = outcomesData.filter((o) => o.prediction_outcome === 'TN').length;
      const fn = outcomesData.filter((o) => o.prediction_outcome === 'FN').length;

      const behaviorByRisk = {
        high: { bookedNow: 0, delayed: 0, abandoned: 0, total: 0 },
        medium: { bookedNow: 0, delayed: 0, abandoned: 0, total: 0 },
        low: { bookedNow: 0, delayed: 0, abandoned: 0, total: 0 }
      };

      (decisionsData || []).forEach((d) => {
        const riskLevel =
          Number(d.regret_risk_score) >= 0.7
            ? 'high'
            : Number(d.regret_risk_score) >= 0.4
            ? 'medium'
            : 'low';

        behaviorByRisk[riskLevel].total += 1;

        if (d.user_action === 'booked_now') {
          behaviorByRisk[riskLevel].bookedNow += 1;
        } else if (d.user_action === 'delayed') {
          behaviorByRisk[riskLevel].delayed += 1;
        } else if (d.user_action === 'abandoned') {
          behaviorByRisk[riskLevel].abandoned += 1;
        }
      });

      Object.keys(behaviorByRisk).forEach((level) => {
        const total = behaviorByRisk[level].total;
        if (total > 0) {
          behaviorByRisk[level].bookedNow = Math.round(
            (behaviorByRisk[level].bookedNow / total) * 100
          );
          behaviorByRisk[level].delayed = Math.round(
            (behaviorByRisk[level].delayed / total) * 100
          );
          behaviorByRisk[level].abandoned = Math.round(
            (behaviorByRisk[level].abandoned / total) * 100
          );
        }
      });

      setAnalyticsData({
        precision: tp + fp > 0 ? Math.round((tp / (tp + fp)) * 100) : 0,
        recall: tp + fn > 0 ? Math.round((tp / (tp + fn)) * 100) : 0,
        falsePositiveRate: fp + tn > 0 ? Math.round((fp / (fp + tn)) * 100) : 0,
        userBehaviorByRisk: behaviorByRisk
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateApiKey = async () => {
    if (!newKeyName.trim()) {
      alert('Please enter a key name');
      return;
    }

    try {
      const key =
        'mzr_' +
        newKeyEnv +
        '_' +
        Array.from(crypto.getRandomValues(new Uint8Array(24)))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');

      const { error } = await supabase.from('api_keys_v2').insert({
        user_id: user.id,
        key_name: newKeyName,
        api_key: key,
        environment: newKeyEnv,
        is_active: true
      });

      if (error) throw error;

      setGeneratedKey(key);
      setNewKeyName('');
      setShowNewKeyForm(false);
      fetchApiKeys();
    } catch (err) {
      console.error('Error generating API key:', err);
      alert('Failed to generate API key. Please try again.');
    }
  };

  const deleteApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('api_keys_v2')
        .delete()
        .eq('id', keyId);

      if (error) throw error;
      fetchApiKeys();
    } catch (err) {
      console.error('Error deleting API key:', err);
      alert('Failed to delete API key');
    }
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const getRiskLabel = (score) => {
    if (score >= 70) return 'High';
    if (score >= 40) return 'Medium';
    return 'Low';
  };

  const getRiskColor = (score) => {
    if (score >= 70) return 'var(--accent-red)';
    if (score >= 40) return 'var(--accent-primary)';
    return 'var(--accent-green)';
  };

  const handleTabChange = (tab) => {
    if (!hasConsoleAccess && tab !== 'billing') {
      setActiveTab('billing');
      return;
    }
    setActiveTab(tab);
  };

  return (
    <div className="console">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root,
        :root[data-theme="light"] {
          --bg-primary: #F4F1EB;
          --bg-secondary: #FDFCF8;
          --bg-tertiary: #EAE7DF;
          --bg-glass: #FDFCF8;
          --text-primary: #1C1917;
          --text-secondary: #6B6560;
          --text-muted: #A8A19A;
          --accent-primary: #D97642;
          --accent-green: #3BA876;
          --accent-red: #C0392B;
          --accent-blue: #2F6B9A;
          --border-primary: rgba(28,25,23,0.12);
          --border-subtle: rgba(28,25,23,0.08);
          --border-glow: rgba(217,118,66,0.25);
          --border: rgba(28,25,23,0.09);
          --ink: #1C1917;
          --ink2: #6B6560;
          --ink3: #A8A19A;
          --fire: #D97642;
          --line: rgba(28,25,23,0.09);
        }

        :root[data-theme="dark"] {
          --bg-primary: #100E0C;
          --bg-secondary: #1A1714;
          --bg-tertiary: #242018;
          --bg-glass: #1A1714;
          --text-primary: #F0EDE6;
          --text-secondary: #9E9890;
          --text-muted: #665F58;
          --accent-primary: #D97642;
          --accent-green: #3BA876;
          --accent-red: #E05C4B;
          --accent-blue: #5A9CC5;
          --border-primary: rgba(240,237,230,0.1);
          --border-subtle: rgba(240,237,230,0.07);
          --border-glow: rgba(217,118,66,0.2);
          --border: rgba(240,237,230,0.08);
          --ink: #F0EDE6;
          --ink2: #9E9890;
          --ink3: #665F58;
          --fire: #D97642;
          --line: rgba(240,237,230,0.08);
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .console {
          min-height: 100vh;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
        }

        .content {
          flex: 1;
          max-width: 1280px;
          margin: 0 auto;
          padding: 2rem 1.5rem;
          width: 100%;
        }

        .tabs {
          display: flex;
          gap: 0;
          margin-bottom: 2.5rem;
          border-bottom: 1px solid var(--border-subtle);
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .tabs::-webkit-scrollbar {
          display: none;
        }

        .tab {
          padding: 0.75rem 1.25rem;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          color: var(--text-muted);
          font-family: 'Manrope', sans-serif;
          font-weight: 600;
          font-size: 0.8rem;
          letter-spacing: 0.04em;
          cursor: pointer;
          transition: color 0.15s, border-color 0.15s;
          display: flex;
          align-items: center;
          gap: 0.45rem;
          margin-bottom: -1px;
          white-space: nowrap;
        }

        .tab:hover {
          color: var(--text-primary);
        }

        .tab.active {
          color: var(--accent-primary);
          border-bottom-color: var(--accent-primary);
        }

        .tab.locked {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .tab.locked:hover {
          color: var(--text-muted);
        }

        .console-paywall-note {
          margin: 0 0 1.5rem;
          padding: 1rem 1.125rem;
          background: rgba(217,118,66,0.08);
          border: 1px solid rgba(217,118,66,0.25);
          border-radius: 0.5rem;
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .glass-card,
        .card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: 0.625rem;
          transition: border-color 0.15s;
        }

        .glass-card:hover,
        .card:hover {
          border-color: var(--border-primary);
        }

        .stat-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: 0.625rem;
          padding: 1.5rem;
          transition: border-color 0.15s;
        }

        .stat-card:hover {
          border-color: var(--border-primary);
        }

        .stat-header {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          margin-bottom: 0.875rem;
        }

        .stat-icon {
          width: 36px;
          height: 36px;
          border-radius: 0.4rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-tertiary);
          flex-shrink: 0;
        }

        .stat-label {
          font-family: 'Manrope', sans-serif;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--text-secondary);
        }

        .stat-value {
          font-family: 'Manrope', sans-serif;
          font-size: 2.25rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: var(--text-primary);
          margin-bottom: 0.25rem;
          line-height: 1;
        }

        .stat-sublabel {
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .route-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .route-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.65rem 0.875rem;
          background: var(--bg-tertiary);
          border-radius: 0.4rem;
          transition: background 0.15s;
        }

        .route-item:hover {
          background: var(--border-subtle);
        }

        .route-name {
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 0.9rem;
          letter-spacing: 0.02em;
        }

        .route-stats {
          display: flex;
          gap: 1rem;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        .form-label {
          font-family: 'Manrope', sans-serif;
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--text-secondary);
        }

        .form-input {
          padding: 0.7rem 0.875rem;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-primary);
          border-radius: 0.375rem;
          color: var(--text-primary);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          transition: border-color 0.15s;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .form-input::placeholder {
          color: var(--text-muted);
        }

        .btn-primary {
          padding: 0.7rem 1.375rem;
          background: var(--text-primary);
          border: 1px solid var(--text-primary);
          border-radius: 0.375rem;
          color: var(--bg-primary);
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: opacity 0.15s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
        }

        .btn-primary:hover {
          opacity: 0.8;
        }

        .btn-primary:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .btn-secondary {
          padding: 0.7rem 1.375rem;
          background: transparent;
          border: 1px solid var(--border-primary);
          border-radius: 0.375rem;
          color: var(--text-secondary);
          font-family: 'DM Sans', sans-serif;
          font-weight: 500;
          font-size: 0.875rem;
          cursor: pointer;
          transition: border-color 0.15s, color 0.15s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
        }

        .btn-secondary:hover {
          border-color: var(--accent-primary);
          color: var(--text-primary);
        }

        .explanation-box {
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: 0.625rem;
          padding: 1.5rem;
          margin-bottom: 1.75rem;
        }

        .explanation-title {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 1rem;
          margin-bottom: 0.75rem;
        }

        .explanation-text {
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.65;
          margin-bottom: 1rem;
        }

        .risk-levels {
          display: flex;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .risk-level-item {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          font-size: 0.85rem;
        }

        .risk-level-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        .risk-level-dot.high {
          background: var(--accent-red);
        }

        .risk-level-dot.medium {
          background: var(--accent-primary);
        }

        .risk-level-dot.low {
          background: var(--accent-green);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.75rem;
        }

        .signals-container {
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: 0.625rem;
          overflow: hidden;
        }

        .signals-header {
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--border-subtle);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .signals-title {
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
        }

        .header-actions {
          display: flex;
          gap: 0.5rem;
        }

        .icon-btn {
          padding: 0.45rem;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-primary);
          border-radius: 0.375rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: border-color 0.15s;
          color: var(--text-secondary);
        }

        .icon-btn:hover {
          border-color: var(--accent-primary);
          color: var(--text-primary);
        }

        .filter-panel {
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--border-subtle);
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          background: var(--bg-tertiary);
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }

        .filter-label {
          font-family: 'Manrope', sans-serif;
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--text-secondary);
        }

        .filter-select {
          padding: 0.45rem 0.7rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 0.35rem;
          color: var(--text-primary);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.875rem;
          cursor: pointer;
        }

        .signals-list {
          max-height: 600px;
          overflow-y: auto;
        }

        .signal-row {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--border-subtle);
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: background 0.15s;
        }

        .signal-row:last-child {
          border-bottom: none;
        }

        .signal-row:hover {
          background: var(--bg-tertiary);
        }

        .signal-route {
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 1rem;
          letter-spacing: 0.02em;
        }

        .signal-details {
          display: flex;
          gap: 1.25rem;
          flex-wrap: wrap;
          margin-top: 0.4rem;
        }

        .signal-detail {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.825rem;
          color: var(--text-secondary);
        }

        .risk-badge {
          padding: 0.35rem 0.75rem;
          border-radius: 0.35rem;
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .risk-score {
          font-family: 'Manrope', sans-serif;
          font-size: 1.375rem;
          font-weight: 800;
          letter-spacing: -0.01em;
          margin-top: 0.25rem;
        }

        .api-keys-section {
          max-width: 860px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .section-title {
          font-family: 'Manrope', sans-serif;
          font-weight: 800;
          font-size: 1.375rem;
          letter-spacing: -0.01em;
        }

        .empty-state {
          text-align: center;
          padding: 3rem 1.5rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: 0.625rem;
        }

        .empty-state-icon {
          margin: 0 auto 1rem;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: var(--bg-tertiary);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .key-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: 0.625rem;
          padding: 1.375rem;
          margin-bottom: 0.75rem;
          transition: border-color 0.15s;
        }

        .key-card:hover {
          border-color: var(--border-primary);
        }

        .key-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.875rem;
        }

        .key-info h3 {
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 1rem;
          margin-bottom: 0.2rem;
        }

        .key-meta {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .key-actions {
          display: flex;
          gap: 0.5rem;
        }

        .icon-btn-small {
          padding: 0.4rem;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-primary);
          border-radius: 0.3rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: border-color 0.15s;
          color: var(--text-secondary);
        }

        .icon-btn-small:hover {
          border-color: var(--accent-primary);
        }

        .key-value {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.65rem 0.875rem;
          background: var(--bg-tertiary);
          border-radius: 0.375rem;
        }

        .key-value code {
          flex: 1;
          font-family: 'Courier New', monospace;
          font-size: 0.825rem;
          color: var(--text-primary);
          word-break: break-all;
        }

        .new-key-form {
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: 0.625rem;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .form-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 1rem;
        }

        .generated-key-alert {
          background: rgba(59,168,118,0.07);
          border: 1px solid rgba(59,168,118,0.3);
          border-radius: 0.625rem;
          padding: 1.375rem;
          margin-bottom: 1.5rem;
        }

        .alert-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 0.9rem;
          color: var(--accent-green);
          margin-bottom: 0.75rem;
        }

        .analytics-grid {
          display: grid;
          gap: 1.5rem;
        }

        .behavior-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }

        .behavior-card {
          background: var(--bg-tertiary);
          padding: 1.25rem;
          border-radius: 0.5rem;
          border: 1px solid var(--border-subtle);
        }

        .behavior-title {
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        .behavior-stat {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.45rem 0;
          border-bottom: 1px solid var(--border-subtle);
        }

        .behavior-stat:last-child {
          border-bottom: none;
        }

        .behavior-label {
          font-size: 0.825rem;
          color: var(--text-secondary);
        }

        .behavior-value {
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 1rem;
        }

        .docs-content {
          max-width: 760px;
        }

        .docs-section {
          margin-bottom: 2.5rem;
        }

        .docs-heading {
          font-family: 'Manrope', sans-serif;
          font-weight: 800;
          font-size: 1.5rem;
          letter-spacing: -0.01em;
          margin-bottom: 0.875rem;
        }

        .docs-text {
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.75;
          margin-bottom: 1.25rem;
        }

        .code-snippet {
          background: var(--bg-tertiary);
          border: 1px solid var(--border-primary);
          border-radius: 0.4rem;
          padding: 1rem 1.125rem;
          margin-top: 0.875rem;
          overflow-x: auto;
        }

        .code-snippet pre {
          font-family: 'Courier New', monospace;
          font-size: 0.8rem;
          color: var(--text-primary);
          line-height: 1.65;
          overflow-x: auto;
        }

        .billing-tier-tabs {
          display: flex;
          gap: 10px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          padding-bottom: 4px;
          margin-bottom: 16px;
        }

        .billing-tier-tabs::-webkit-scrollbar {
          display: none;
        }

        .billing-tier-tab {
          min-width: 150px;
          flex: 0 0 auto;
          text-align: left;
          padding: 12px 14px;
          border-radius: 8px;
          border: 1px solid var(--line);
          background: transparent;
          color: var(--ink2);
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s, color 0.15s;
        }

        .billing-tier-tab.active {
          border-color: var(--fire);
          background: rgba(217,118,66,0.08);
          color: var(--fire);
        }

        .billing-tier-tab-name {
          font-family: 'Manrope', sans-serif;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 4px;
        }

        .billing-tier-tab-price {
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 3px;
        }

        .billing-tier-tab-note {
          font-size: 11px;
          opacity: 0.8;
        }

        @media (max-width: 768px) {
          .content {
            padding: 1rem;
          }

          .tab {
            padding: 0.6rem 0.875rem;
            font-size: 0.72rem;
          }

          .tab span {
            display: none;
          }

          .stat-value {
            font-size: 1.875rem;
          }

          .signal-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.875rem;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.875rem;
          }

          .btn-primary,
          .btn-secondary {
            width: 100%;
          }

          .form-actions {
            flex-direction: column;
          }

          .key-header {
            flex-direction: column;
            gap: 0.875rem;
          }

          .behavior-grid {
            grid-template-columns: 1fr;
          }

          .billing-tier-tab {
            min-width: 140px;
          }
        }
      `}</style>

      <Navigation />

      <div className="content">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''} ${
              !hasConsoleAccess ? 'locked' : ''
            }`}
            onClick={() => handleTabChange('overview')}
          >
            <Activity size={16} />
            <span>Overview</span>
          </button>

          <button
            className={`tab ${activeTab === 'signals' ? 'active' : ''} ${
              !hasConsoleAccess ? 'locked' : ''
            }`}
            onClick={() => handleTabChange('signals')}
          >
            <Radio size={16} />
            <span>Live Signals</span>
          </button>

          <button
            className={`tab ${activeTab === 'api-keys' ? 'active' : ''} ${
              !hasConsoleAccess ? 'locked' : ''
            }`}
            onClick={() => handleTabChange('api-keys')}
          >
            <Key size={16} />
            <span>API Keys</span>
          </button>

          <button
            className={`tab ${activeTab === 'analytics' ? 'active' : ''} ${
              !hasConsoleAccess ? 'locked' : ''
            }`}
            onClick={() => handleTabChange('analytics')}
          >
            <TrendingUp size={16} />
            <span>Analytics</span>
          </button>

          <button
            className={`tab ${activeTab === 'demo' ? 'active' : ''} ${
              !hasConsoleAccess ? 'locked' : ''
            }`}
            onClick={() => handleTabChange('demo')}
          >
            <Play size={16} />
            <span>Quick Demo</span>
          </button>

          <button
            className={`tab ${activeTab === 'docs' ? 'active' : ''} ${
              !hasConsoleAccess ? 'locked' : ''
            }`}
            onClick={() => handleTabChange('docs')}
          >
            <FileText size={16} />
            <span>Docs</span>
          </button>

          <button
            className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
            onClick={() => handleTabChange('billing')}
          >
            <CreditCard size={16} />
            <span>Billing</span>
          </button>
        </div>

        {!hasConsoleAccess && billingLoaded && (
          <div className="console-paywall-note">
            Your console is active, but product functions stay locked until you start a
            trial or activate a subscription. Use the Billing tab to continue.
          </div>
        )}

        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem'
              }}
            >
              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon">
                    <Target size={18} color="var(--accent-primary)" />
                  </div>
                  <div className="stat-label">Decisions Supported</div>
                </div>
                <div className="stat-value">
                  {overviewStats.decisionsSupported.toLocaleString()}
                </div>
                <div className="stat-sublabel">This month</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon">
                    <TrendingUp size={18} color="var(--accent-green)" />
                  </div>
                  <div className="stat-label">Estimated Savings</div>
                </div>
                <div className="stat-value">
                  £{overviewStats.estimatedSavings.toLocaleString()}
                </div>
                <div className="stat-sublabel">Based on high-risk avoidance</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon">
                    <Zap size={18} color="var(--accent-red)" />
                  </div>
                  <div className="stat-label">High-Risk Signals</div>
                </div>
                <div className="stat-value">{overviewStats.highRiskSignals}</div>
                <div className="stat-sublabel">Book now recommendations</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon">
                    <CheckCircle size={18} color="var(--accent-blue)" />
                  </div>
                  <div className="stat-label">Conversion Rate</div>
                </div>
                <div className="stat-value">{overviewStats.conversionRate}%</div>
                <div className="stat-sublabel">Booked immediately</div>
              </div>
            </div>

            <div className="glass-card" style={{ padding: '1.75rem' }}>
              <h3
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  marginBottom: '1.25rem'
                }}
              >
                Top Routes This Month
              </h3>

              <div className="route-list">
                {topRoutes.length > 0 ? (
                  topRoutes.map((route, idx) => (
                    <div key={idx} className="route-item">
                      <div className="route-name">{route.route}</div>
                      <div className="route-stats">
                        <span>{route.decisions} decisions</span>
                        <span
                          style={{
                            color:
                              route.highRiskPct >= 70
                                ? 'var(--accent-red)'
                                : route.highRiskPct >= 40
                                ? 'var(--accent-primary)'
                                : 'var(--accent-green)'
                          }}
                        >
                          {route.highRiskPct}% high
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '1.5rem',
                      color: 'var(--text-muted)',
                      fontSize: '0.875rem'
                    }}
                  >
                    No route data yet
                  </div>
                )}
              </div>
            </div>

            <APIPlayground />
          </div>
        )}

        {activeTab === 'signals' && (
          <>
            <div className="explanation-box">
              <div className="explanation-title">
                <Info size={16} />
                Understanding Risk Scores
              </div>

              <div className="explanation-text">
                RegretRisk scores predict the likelihood of a 10%+ price increase within
                7 days.
              </div>

              <div className="risk-levels">
                <div className="risk-level-item">
                  <div className="risk-level-dot high"></div>
                  <span>
                    <strong>High (70-100%):</strong> Book now — prices likely to rise
                  </span>
                </div>

                <div className="risk-level-item">
                  <div className="risk-level-dot medium"></div>
                  <span>
                    <strong>Medium (40-69%):</strong> Monitor closely
                  </span>
                </div>

                <div className="risk-level-item">
                  <div className="risk-level-dot low"></div>
                  <span>
                    <strong>Low (0-39%):</strong> Safe to wait
                  </span>
                </div>
              </div>
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Active Routes</div>
                <div className="stat-value">{stats.activeRoutes}</div>
                <div className="stat-sublabel">Unique routes</div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Avg Risk Score</div>
                <div className="stat-value">{stats.avgRisk}%</div>
                <div className="stat-sublabel">Across all routes</div>
              </div>

              <div className="stat-card">
                <div className="stat-label">High Risk</div>
                <div className="stat-value" style={{ color: 'var(--accent-red)' }}>
                  {stats.highRiskCount}
                </div>
                <div className="stat-sublabel">Routes ≥70%</div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Predictions</div>
                <div className="stat-value">{stats.totalSnapshots}</div>
                <div className="stat-sublabel">In view</div>
              </div>
            </div>

            <div className="signals-container">
              <div className="signals-header">
                <div className="signals-title">Live Signals</div>
                <div className="header-actions">
                  <button className="icon-btn" onClick={() => setShowFilters(!showFilters)}>
                    <Filter size={16} />
                  </button>
                  <button className="icon-btn" onClick={fetchLiveData}>
                    <RefreshCw size={16} />
                  </button>
                </div>
              </div>

              {showFilters && (
                <div className="filter-panel">
                  <div className="filter-group">
                    <label className="filter-label">Date Range</label>
                    <select
                      className="filter-select"
                      value={filters.date}
                      onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                    >
                      <option value="latest">Latest</option>
                      <option value="today">Today</option>
                      <option value="week">Past Week</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Origin</label>
                    <select
                      className="filter-select"
                      value={filters.origin}
                      onChange={(e) => setFilters({ ...filters, origin: e.target.value })}
                    >
                      <option value="all">All Airports</option>
                      {['MAN', 'LGW', 'LHR', 'EDI', 'BRS', 'LPL', 'BHX', 'NCL', 'GLA'].map(
                        (a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Risk Level</label>
                    <select
                      className="filter-select"
                      value={filters.risk}
                      onChange={(e) => setFilters({ ...filters, risk: e.target.value })}
                    >
                      <option value="all">All Levels</option>
                      <option value="high">High Risk Only</option>
                      <option value="medium">Medium Risk Only</option>
                      <option value="low">Low Risk Only</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="signals-list">
                {loading ? (
                  <div
                    style={{
                      padding: '3rem',
                      textAlign: 'center',
                      color: 'var(--text-muted)',
                      fontSize: '0.875rem'
                    }}
                  >
                    Loading signals...
                  </div>
                ) : signals.length === 0 ? (
                  <div
                    style={{
                      padding: '3rem',
                      textAlign: 'center',
                      color: 'var(--text-muted)',
                      fontSize: '0.875rem'
                    }}
                  >
                    No signals found
                  </div>
                ) : (
                  signals.map((signal, idx) => (
                    <div key={idx} className="signal-row">
                      <div>
                        <div className="signal-route">
                          {signal.origin_iata} → {signal.destination_iata}
                        </div>
                        <div className="signal-details">
                          <div className="signal-detail">
                            <Calendar size={13} />
                            {signal.outbound_date}
                          </div>
                          <div className="signal-detail">
                            £{Number(signal.price_gbp || 0).toFixed(2)}
                          </div>
                          <div className="signal-detail">{signal.dtd} days out</div>
                          <div className="signal-detail">{signal.recommendation}</div>
                          <div className="signal-detail">{signal.confidence} confidence</div>
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <div
                          className="risk-badge"
                          style={{
                            background: `${getRiskColor(signal.riskScore)}18`,
                            color: getRiskColor(signal.riskScore)
                          }}
                        >
                          {getRiskLabel(signal.riskScore)}
                        </div>
                        <div
                          className="risk-score"
                          style={{ color: getRiskColor(signal.riskScore) }}
                        >
                          {signal.riskScore}%
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'api-keys' && (
          <div className="api-keys-section">
            <div className="section-header">
              <h2 className="section-title">API Keys</h2>
              {apiKeys.length > 0 && !showNewKeyForm && (
                <button className="btn-primary" onClick={() => setShowNewKeyForm(true)}>
                  <Plus size={16} />
                  Generate New Key
                </button>
              )}
            </div>

            {generatedKey && (
              <div className="generated-key-alert">
                <div className="alert-title">
                  <CheckCircle size={18} />
                  API Key Generated
                </div>

                <div
                  style={{
                    fontSize: '0.825rem',
                    color: 'var(--text-secondary)',
                    marginBottom: '0.875rem'
                  }}
                >
                  Save this key now. It won't be shown again.
                </div>

                <div className="key-value">
                  <code>{generatedKey}</code>
                  <button
                    className="icon-btn-small"
                    onClick={() => copyToClipboard(generatedKey)}
                  >
                    <Copy size={15} />
                  </button>
                </div>

                <button
                  className="btn-secondary"
                  onClick={() => setGeneratedKey(null)}
                  style={{ marginTop: '0.875rem' }}
                >
                  Done
                </button>
              </div>
            )}

            {showNewKeyForm && (
              <div className="new-key-form">
                <h3
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    marginBottom: '1rem',
                    fontSize: '1rem'
                  }}
                >
                  Create New API Key
                </h3>

                <div className="form-group">
                  <label className="form-label">Key Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="Production API, Development, etc."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Environment</label>
                  <select
                    className="form-input"
                    value={newKeyEnv}
                    onChange={(e) => setNewKeyEnv(e.target.value)}
                  >
                    <option value="live">Live</option>
                    <option value="test">Test</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button className="btn-primary" onClick={generateApiKey}>
                    Generate Key
                  </button>
                  <button className="btn-secondary" onClick={() => setShowNewKeyForm(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {apiKeys.length === 0 && !showNewKeyForm ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <Key size={28} color="var(--accent-primary)" />
                </div>

                <div
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    marginBottom: '0.5rem'
                  }}
                >
                  No API Keys Yet
                </div>

                <div
                  style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    marginBottom: '1.5rem'
                  }}
                >
                  Generate your first API key to get started
                </div>

                <button className="btn-primary" onClick={() => setShowNewKeyForm(true)}>
                  <Plus size={16} />
                  Generate API Key
                </button>
              </div>
            ) : (
              <div>
                {apiKeys.map((key) => (
                  <div key={key.id} className="key-card">
                    <div className="key-header">
                      <div className="key-info">
                        <h3>{key.key_name}</h3>
                        <div className="key-meta">
                          Created {new Date(key.created_at).toLocaleDateString()} ·{' '}
                          {key.environment}
                        </div>
                      </div>

                      <div className="key-actions">
                        <button
                          className="icon-btn-small"
                          onClick={() => deleteApiKey(key.id)}
                        >
                          <Trash2 size={15} color="var(--accent-red)" />
                        </button>
                      </div>
                    </div>

                    <div className="key-value">
                      <code>{key.api_key}</code>
                      <button
                        className="icon-btn-small"
                        onClick={() => copyToClipboard(key.api_key)}
                      >
                        <Copy size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-grid">
            <div>
              <h2 className="section-title" style={{ marginBottom: '1.5rem' }}>
                Signal Performance
              </h2>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}
              >
                <div className="stat-card">
                  <div className="stat-label">Precision (High-Risk)</div>
                  <div className="stat-value">{analyticsData.precision}%</div>
                  <div className="stat-sublabel">Correct high-risk signals</div>
                </div>

                <div className="stat-card">
                  <div className="stat-label">Recall</div>
                  <div className="stat-value">{analyticsData.recall}%</div>
                  <div className="stat-sublabel">Caught price increases</div>
                </div>

                <div className="stat-card">
                  <div className="stat-label">False Positive Rate</div>
                  <div className="stat-value">{analyticsData.falsePositiveRate}%</div>
                  <div className="stat-sublabel">Incorrect alerts</div>
                </div>
              </div>
            </div>

            <div className="glass-card" style={{ padding: '1.75rem' }}>
              <h3
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  marginBottom: '0.5rem'
                }}
              >
                User Behaviour by Signal Strength
              </h3>

              <div className="behavior-grid">
                {[
                  {
                    label: 'High Risk (0.70–1.00)',
                    color: 'var(--accent-red)',
                    data: analyticsData.userBehaviorByRisk.high
                  },
                  {
                    label: 'Medium Risk (0.40–0.69)',
                    color: 'var(--accent-primary)',
                    data: analyticsData.userBehaviorByRisk.medium
                  },
                  {
                    label: 'Low Risk (0.00–0.39)',
                    color: 'var(--accent-green)',
                    data: analyticsData.userBehaviorByRisk.low
                  }
                ].map(({ label, color, data }) => (
                  <div key={label} className="behavior-card">
                    <div className="behavior-title" style={{ color }}>
                      {label}
                    </div>

                    {[
                      ['Booked Now', data.bookedNow],
                      ['Delayed', data.delayed],
                      ['Abandoned', data.abandoned]
                    ].map(([k, v]) => (
                      <div key={k} className="behavior-stat">
                        <span className="behavior-label">{k}</span>
                        <span className="behavior-value">{v}%</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <CSVReportDownload />
            <VerifiedOutcomesShell />
          </div>
        )}

        {activeTab === 'demo' && (
          <div>
            <h2 className="section-title" style={{ marginBottom: '0.5rem' }}>
              Quick Demo
            </h2>

            <p
              style={{
                color: 'var(--text-secondary)',
                fontSize: '0.9rem',
                marginBottom: '1.5rem',
                lineHeight: 1.65
              }}
            >
              Test the RegretRisk signal on any route. No code required — built for
              non-technical stakeholders.
            </p>

            <DemoWidget />
          </div>
        )}

        {activeTab === 'docs' && (
          <div className="docs-content">
            <h2 className="docs-heading">Quick Start</h2>
            <p className="docs-text">
              Get started with MIZAR's pricing intelligence API in minutes.
            </p>

            <div className="docs-section">
              <h3
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  marginBottom: '0.75rem'
                }}
              >
                1. Get Your API Key
              </h3>

              <p className="docs-text">
                Generate an API key in the{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleTabChange('api-keys');
                  }}
                  style={{
                    color: 'var(--accent-primary)',
                    textDecoration: 'none',
                    fontWeight: 600
                  }}
                >
                  API Keys
                </a>{' '}
                tab.
              </p>
            </div>

            <div className="docs-section">
              <h3
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  marginBottom: '0.75rem'
                }}
              >
                2. Make Your First Request
              </h3>

              <div className="code-snippet">
                <pre>{`curl -X POST https://mizar-api.vercel.app/v1/signal \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "origin": "LHR",
    "destination": "BCN",
    "outbound_date": "2026-07-15",
    "return_date": "2026-07-22",
    "price_gbp": 285.50,
    "session_id": "anon_hash_xyz123"
  }'`}</pre>
              </div>
            </div>

            <div className="docs-section">
              <h3
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  marginBottom: '0.75rem'
                }}
              >
                3. Handle the Response
              </h3>

              <div className="code-snippet">
                <pre>{`{
  "decision_id": "550e8400-e29b-41d4-a716-446655440000",
  "route": "LHR → BCN",
  "recommendation": "book_now",
  "confidence": "high",
  "confidence_score": 0.85,
  "regret_risk_score": 0.73,
  "model_version": "v3_1_0"
}`}</pre>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <BillingTab
            supabase={supabase}
            user={user}
            onBillingRefresh={(billing) => {
              const allowedStatuses = ['trialing', 'active'];
              setHasConsoleAccess(
                !!(billing?.tier && allowedStatuses.includes(billing.subscription_status))
              );
              setBillingLoaded(true);
            }}
          />
        )}
      </div>

      <Footer />
    
</div>
  );
}